import { type TokenId, TOKEN_IDS, LOCK_TOKEN_IDS } from "@/types";
import {
    useDialogPortal,
    useAnnualProjectedEarnings,
    useTransactionFees,
    useGasEstimation,
    useTransactionExecution,
    useProtocolStore,
} from "@/hooks";
import { lockEventEmitter, LOCK_EVENTS } from "@/events";

import {
    gatewayMintAndLockTransaction,
    estimateMintAndLockTransactionGas,
    prepareMintAndLockTransaction,
    prepareUnstakeAndLockTransaction,
    estimateUnstakeAndLockTransactionGas,
    prepareCreatePositionTransaction,
    estimateCreatePositionTransactionGas,
    gatewayCreatePositionTransaction,
    gatewayUnstakeAndLockTransaction,
    prepareZapInAndLockTransaction,
} from "@/services";
import { AppError } from "@/types";
import { useCallback } from "react";
import { getContract, type WalletClient, parseUnits } from "viem";
import { useTokenApprovalV2 } from "./useTokenApprovalV2";
import { getGatewayContractAddress, getTokenAddress, QUERY_KEYS, TOKEN_CONFIG } from "@/config";
import { truncateDecimals } from "@/utils";
import { useGatewayPendleZap } from "../pendle/useGatewayPendleZap";
import { gatewayV1Abi } from "@/abi";
import { mainnet } from "viem/chains";
import { gatewayV2Abi } from "@/abi/gatewayV2.abi";
import z from "zod";
import { useQueryClient } from "@tanstack/react-query";

interface LockTransactionProps {
    lockAmount: number;
    duration: number;
    lockTokenId: TokenId;
}
import { useAccount } from "wagmi";

export function useLockTransaction({ lockAmount, duration, lockTokenId }: LockTransactionProps) {
    const truncatedLockAmount = truncateDecimals(lockAmount, 6);
    const nTruncatedLockAmount = Number(truncatedLockAmount);

    const { invalidateQueries } = useQueryClient();
    const { address } = useAccount();

    const {
        amountOut: zapAmountOut,
        tokenOut: zapTokenOut,
        requiresZap: isZap,
        isLoading: isZapLoading,
        getPendleSwap,
    } = useGatewayPendleZap({
        sourceTokenId: lockTokenId,
        amount: lockAmount,
        outTokenId: TOKEN_IDS.USDC,
    });

    const { closeDialog } = useDialogPortal();
    const { calculateProjectedEarnings, apy } = useAnnualProjectedEarnings(duration, nTruncatedLockAmount);
    const { isApproving, approve, isApproved } = useTokenApprovalV2({
        tokenId: lockTokenId,
        amount: nTruncatedLockAmount,
        spender: getGatewayContractAddress(),
    });
    const { stats, isLoading } = useProtocolStore();

    const estimateGasFn = useCallback(
        async (walletClient: WalletClient) => {
            if (isZapLoading) throw new Error("Zap data is still loading");
            if (isZap) {
                if (!walletClient.account?.address) throw new Error("Wallet not connected");

                // Get router data for the swap
                const routerData = await getPendleSwap();

                // Get token address
                const tokenAddress = getTokenAddress(mainnet.id, lockTokenId);

                return getContract({
                    address: getGatewayContractAddress(mainnet.id),
                    abi: gatewayV1Abi,
                    client: walletClient,
                }).estimateGas.zapInAndLock(
                    [
                        tokenAddress, // _token address
                        parseUnits(`${lockAmount}`, TOKEN_CONFIG[lockTokenId].decimals), // _amount
                        routerData.swapData.extRouter, // _router
                        routerData.swapData.extCalldata, // _routerData
                        duration, // _unwindingEpochs
                        walletClient.account.address, // _to
                    ],
                    {
                        account: walletClient.account.address,
                    },
                );
            }
            if (lockTokenId === TOKEN_IDS.USDC) {
                return estimateMintAndLockTransactionGas(nTruncatedLockAmount, duration, walletClient);
            }
            if (lockTokenId === TOKEN_IDS.IUSD) {
                return estimateCreatePositionTransactionGas(nTruncatedLockAmount, duration, walletClient);
            }
            if (lockTokenId === TOKEN_IDS.SIUSD) {
                return estimateUnstakeAndLockTransactionGas(nTruncatedLockAmount, duration, walletClient);
            }
            throw new AppError("Invalid token for gas estimation");
        },
        [nTruncatedLockAmount, duration, lockTokenId],
    );

    const prepareTransactionFn = useCallback(
        async (walletClient: WalletClient) => {
            if (lockTokenId === TOKEN_IDS.USDC) {
                return prepareMintAndLockTransaction(nTruncatedLockAmount, duration, walletClient);
            }
            if (lockTokenId === TOKEN_IDS.IUSD) {
                return prepareCreatePositionTransaction(nTruncatedLockAmount, duration, walletClient);
            }
            if (lockTokenId === TOKEN_IDS.SIUSD) {
                return prepareUnstakeAndLockTransaction(nTruncatedLockAmount, duration, walletClient);
            }
            if (lockTokenId in LOCK_TOKEN_IDS) {
                if (!walletClient.account?.address) throw new Error("Wallet not connected");
                return prepareZapInAndLockTransaction(
                    lockTokenId,
                    nTruncatedLockAmount,
                    duration,
                    walletClient.account?.address,
                    walletClient,
                );
            }
            throw new AppError("Invalid token for lock");
        },
        [truncatedLockAmount, duration, lockTokenId],
    );

    const {
        gasEstimate,
        isEstimatingGas,
        error: gasError,
    } = useGasEstimation({
        estimateGasFn,
        prepareTransactionFn,
        tokenId: lockTokenId,
    });

    const { gasPriceUSD, slippagePercent } = useTransactionFees({
        estimatedGas: gasEstimate,
    });

    const executeTransactionFn = useCallback(
        async (walletClient: WalletClient) => {
            if (isZapLoading) throw new Error("Zap data is still loading");
            if (isZap) {
                if (!walletClient.account?.address) throw new Error("Wallet not connected");
                if (!zapAmountOut) throw new Error("Amount out is not defined");

                // Get router data for the swap
                const pendleSwap = await getPendleSwap();

                // Get token address
                const tokenAddress = getTokenAddress(mainnet.id, lockTokenId);

                // Create a Zod schema for the arguments
                const zapInAndLockArgsSchema = z.tuple([
                    z.string().regex(/^0x[a-fA-F0-9]{40}$/i, "Invalid token address"),
                    z.any().refine((val) => typeof val === "bigint", "Amount must be a bigint"),
                    z.string().regex(/^0x[a-fA-F0-9]{40}$/i, "Invalid router address"),
                    z.string().startsWith("0x", "Invalid router data"),
                    z.number().int().positive("Duration must be a positive integer"),
                    z.string().regex(/^0x[a-fA-F0-9]{40}$/i, "Invalid recipient address"),
                ]);

                // Create the arguments array
                const args = [
                    tokenAddress, // _token address
                    parseUnits(`${lockAmount}`, TOKEN_CONFIG[lockTokenId].decimals), // _amount
                    pendleSwap.tx.to, // _router
                    pendleSwap.tx.data, // _routerData
                    duration, // _unwindingEpochs
                    walletClient.account.address, // _to
                ];

                // Validate args
                const validationResult = zapInAndLockArgsSchema.safeParse(args);
                if (!validationResult.success) {
                    throw new AppError(`Invalid zapInAndLock arguments: ${validationResult.error.message}`);
                }

                return getContract({
                    address: getGatewayContractAddress(mainnet.id),
                    abi: gatewayV2Abi,
                    client: walletClient,
                }).write.zapInAndLock(args, {
                    account: walletClient.account.address,
                });
            }
            if (lockTokenId === TOKEN_IDS.USDC) {
                return gatewayMintAndLockTransaction(nTruncatedLockAmount, duration, walletClient);
            }
            if (lockTokenId === TOKEN_IDS.IUSD) {
                return gatewayCreatePositionTransaction(nTruncatedLockAmount, duration, walletClient);
            }
            if (lockTokenId === TOKEN_IDS.SIUSD) {
                return gatewayUnstakeAndLockTransaction(nTruncatedLockAmount, duration, walletClient);
            }

            throw new AppError("Invalid token for lock");
        },
        [nTruncatedLockAmount, duration, lockTokenId, approve, isZap, isZapLoading, zapAmountOut],
    );

    const {
        isExecuting,
        error: transactionError,
        executeTransaction: executeLockTransaction,
    } = useTransactionExecution({
        executeTransactionFn,
        onSuccess: (hash) => {
            lockEventEmitter.emit(LOCK_EVENTS.SUCCESS, {
                transactionHash: hash,
                lockTokenId,
                lockAmount: nTruncatedLockAmount,
            });
            closeDialog();
            // Invalidate token balances to refresh
            invalidateQueries({
                queryKey: QUERY_KEYS.TOKENS.BALANCE.LIST(address),
            });
        },
        errorMessage: "Failed to lock tokens",
    });

    const projectedEarnings = calculateProjectedEarnings(nTruncatedLockAmount.toString());

    const { siUSDExchange, liUSDExchange } = (() => {
        if (isLoading) {
            return { siUSDExchange: 0, liUSDExchange: 0 };
        }

        const siUSDExchange = stats.staked.exchangeRateNormalized;
        const liUSDExchange =
            Object.values(stats.locked).find((entry: any) => entry.bucketMaturity === duration)
                ?.exchangeRateNormalized ?? 0;

        return { siUSDExchange, liUSDExchange };
    })();

    return {
        lockAmount,
        gasPriceUSD,
        slippagePercent,
        apy,
        projectedEarnings,
        lockTokenId,
        siUSDExchange,
        liUSDExchange,
        executeLockTransaction,
        isExecuting,
        isLoading: isExecuting || isApproving || isLoading || isZapLoading,
        isEstimatingGas,
        needsApproval: !isApproved,
        error: gasError || transactionError,
        gasEstimate,
        isZap,
        zapTokenOut,
        zapAmountOut,
    };
}
